import React, { useContext, useEffect, useRef, useState } from 'react';
import PositionsCard from '../components/PositionsCard';
import TradeConfigCard from '../components/TradeConfigCard';
import RealSummaryCards from '../components/RealSummaryCards';
import { loadTradeConfig } from '../utils/tradeConfig';
import { getRealSummaryMetrics } from '../utils/realSummary';
import { getVisibleHoldingQuantity } from '../utils/realSummary';
import api from '../api/kiteApi';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

function instrumentKey(position = {}) {
  const exchange = String(position.exchange || '').trim().toUpperCase();
  const symbol = String(position.tradingsymbol || position.trading_symbol || position.tradingSymbol || '').trim().toUpperCase();
  const product = String(position.product || position.product_type || '').trim().toUpperCase();
  if (product === 'CNC' && symbol) return `CNC:${symbol}`;
  if (symbol) return `${exchange}:${symbol}`;
  const token = position.instrument_token ?? position.instrumentToken ?? position.instrument;
  return token == null ? '' : `${exchange}:TOKEN:${Number(token)}`;
}

function getUnifiedLongPositions(openPositions, holdings) {
  const openCncByKey = new Map();
  const otherOpenRows = [];
  openPositions.forEach((position) => {
    if (String(position.product || position.product_type || '').toUpperCase() !== 'CNC') {
      otherOpenRows.push(position);
      return;
    }
    const key = instrumentKey(position);
    if (!key) return;
    const rows = openCncByKey.get(key) || [];
    rows.push(position);
    openCncByKey.set(key, rows);
  });

  const representedKeys = new Set();
  const holdingsByKey = new Map();
  holdings.forEach((holding) => {
    const key = instrumentKey({ ...holding, product: 'CNC' });
    if (!key) return;
    const rows = holdingsByKey.get(key) || [];
    rows.push(holding);
    holdingsByKey.set(key, rows);
  });

  const toCombinedRow = (key, holdingRows, matchingOpen) => {
    const representative = holdingRows[0] || matchingOpen[0];
    if (!representative) return null;
    const liveHoldingQuantity = holdingRows.reduce((sum, holding) => sum + getVisibleHoldingQuantity(holding), 0);
    const holdingQuantity = Math.max(
      liveHoldingQuantity,
      ...holdingRows.map((holding) => Number(holding.combined_holding_quantity) || 0),
    );
    const liveOpenQuantity = matchingOpen.reduce((sum, position) => sum + Math.max(0, Number(position.quantity ?? position.net_quantity) || 0), 0);
    const openQuantity = Math.max(
      liveOpenQuantity,
      ...[...holdingRows, ...matchingOpen].map((row) => Number(row.combined_open_quantity) || 0),
    );
    const combinedQuantity = Math.max(
      holdingQuantity + openQuantity,
      ...[...holdingRows, ...matchingOpen].map((row) => Number(row.combined_quantity) || 0),
    );
    if (!key || combinedQuantity <= 0) return null;

    const holdingNotional = holdingRows.reduce((sum, holding) => (
      sum + getVisibleHoldingQuantity(holding) * (Number(holding.average_price ?? holding.avg_price) || 0)
    ), 0);
    const holdingAverage = Number(representative.average_price ?? representative.avg_price) || 0;
    const openNotional = matchingOpen.reduce((sum, position) => (
      sum + Math.max(0, Number(position.quantity ?? position.net_quantity) || 0)
        * (Number(position.average_price ?? position.avg_price) || 0)
    ), 0);
    const openMatchedQuantity = matchingOpen.reduce((sum, position) => sum + Math.max(0, Number(position.quantity ?? position.net_quantity) || 0), 0);
    const averagePrice = combinedQuantity > 0
      ? (holdingNotional + openNotional) / Math.max(1, liveHoldingQuantity + openMatchedQuantity)
      : holdingAverage;

    return {
      ...representative,
      product: 'CNC',
      asset_type: holdingRows.length ? 'HOLDING' : 'POSITION',
      plan_asset_type: representative.plan_asset_type || (holdingRows.length ? 'HOLDING' : 'POSITION'),
      quantity: combinedQuantity,
      t1_quantity: 0,
      net_quantity: combinedQuantity,
      combined_quantity: combinedQuantity,
      combined_holding_quantity: holdingQuantity,
      combined_open_quantity: openQuantity,
      average_price: averagePrice || holdingAverage,
      avg_price: averagePrice || holdingAverage,
      pnl: holdingRows.reduce((sum, holding) => sum + (Number(holding.pnl) || 0), 0)
        + matchingOpen.reduce((sum, position) => sum + (Number(position.pnl) || 0), 0),
      last_price: representative.last_price ?? representative.close_price ?? matchingOpen[0]?.last_price ?? null,
    };
  };

  const holdingRows = Array.from(holdingsByKey.entries()).map(([key, rows]) => {
    representedKeys.add(key);
    return toCombinedRow(key, rows, openCncByKey.get(key) || []);
  }).filter(Boolean);
  const openOnlyRows = Array.from(openCncByKey.entries())
    .filter(([key]) => !representedKeys.has(key))
    .map(([key, rows]) => toCombinedRow(key, [], rows))
    .filter(Boolean);
  return [...holdingRows, ...openOnlyRows, ...otherOpenRows];
}

export default function Dashboard() {
  // The dashboard is a display layer; keep its broker reads light. The
  // backend live-execution worker continues polling independently at its own
  // configured interval, so this does not delay order triggers.
  const AUTO_REFRESH_MS = 60000;
  const location = useLocation();
  const { isAuthenticated } = useContext(AuthContext);
  const [holdings, setHoldings] = useState([]);
  const [positions, setPositions] = useState({});
  const [realSummary, setRealSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [activeTab, setActiveTab] = useState(() => (new URLSearchParams(location.search).get('tab') === 'config' ? 'config' : 'real'));
  const [tradeConfig, setTradeConfig] = useState(loadTradeConfig);
  const [bulkReset, setBulkReset] = useState(null);
  const [bulkResetMessage, setBulkResetMessage] = useState(null);
  const [bulkResetError, setBulkResetError] = useState(null);
  const [lastDataRefreshAt, setLastDataRefreshAt] = useState(null);
  const loadInFlightRef = useRef(false);

  const load = async () => {
    // The timer and the manual button can fire at the same time. Do not issue
    // overlapping Kite requests, which can make the UI appear to jump
    // backwards when a slower response arrives after a newer one.
    if (loadInFlightRef.current) return;
    loadInFlightRef.current = true;
    setLoading(true);
    setLoadError(false);
    try {
      const [holdingsResult, positionsResult, realSummaryResult] = await Promise.allSettled([
        api.get('/holdings'),
        api.get('/positions'),
        api.get('/risk/summary', { params: { trade_mode: 'REAL' } }),
      ]);

      if (holdingsResult.status === 'fulfilled') {
        setHoldings(holdingsResult.value.data || []);
      } else {
        setHoldings([]);
      }

      if (positionsResult.status === 'fulfilled') {
        setPositions(positionsResult.value.data || {});
      } else {
        setPositions({});
      }

      if (realSummaryResult.status === 'fulfilled') {
        setRealSummary(realSummaryResult.value.data || null);
      } else {
        setRealSummary(null);
      }

      if (holdingsResult.status === 'rejected' && positionsResult.status === 'rejected') {
        setLoadError(true);
      }
      if ([holdingsResult, positionsResult, realSummaryResult].some((result) => result.status === 'fulfilled')) {
        setLastDataRefreshAt(new Date());
      }
    } catch (e) {
      setLoadError(true);
    } finally {
      loadInFlightRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const refreshTimer = window.setInterval(load, AUTO_REFRESH_MS);
    return () => window.clearInterval(refreshTimer);
  }, []);

  useEffect(() => {
    const nextTab = new URLSearchParams(location.search).get('tab') === 'config' ? 'config' : 'real';
    setActiveTab(nextTab);
  }, [location.search]);

  // redirect to login if not authenticated
  const navigate = useNavigate();
  useEffect(() => {
    if (!isAuthenticated) navigate('/real/login');
  }, [isAuthenticated, navigate]);

  const runBulkReset = async (action) => {
    setBulkResetMessage(null);
    setBulkResetError(null);
    const isLive = action.startsWith('live-');
    const isEnable = action.endsWith('enable');
    const isReset = action.endsWith('reset');
    const prompt = isReset
      ? (isLive ? 'Reset live execution state for every saved plan? This does not place orders.' : 'Reset notification history for every saved plan?')
      : `${isEnable ? 'Enable' : 'Disable'} ${isLive ? 'live execution' : 'notifications'} for every saved plan?`;
    if (!window.confirm(prompt)) return;
    setBulkReset(action);
    try {
      const endpoint = isReset
        ? (isLive ? '/live-execution/reset-all' : '/stoplosses/reset-all-notifications')
        : (isLive ? '/live-execution/plans/all' : '/stoplosses/notifications-all');
      const response = isReset
        ? await api.post(endpoint)
        : await api.put(endpoint, isLive
          ? { live_execution_enabled: isEnable }
          : { notifications_enabled: isEnable });
      setBulkResetMessage(response.data?.message || 'Bulk reset completed.');
      await load();
    } catch (requestError) {
      setBulkResetError(requestError.response?.data?.error || requestError.message || 'Bulk reset failed.');
    } finally {
      setBulkReset(null);
    }
  };

  const netPositions = Array.isArray(positions) ? positions : (positions.net || []);
  // Defensive UI guard: only long Kite positions belong on this board, even if
  // an older backend response still contains a short position.
  const openPositions = netPositions.filter((position) => Number(position.quantity ?? position.net_quantity) > 0);
  const unifiedLongPositions = getUnifiedLongPositions(openPositions, holdings);
  const summaryMetrics = getRealSummaryMetrics({ holdings, positions, realSummary });
  const refreshAgeMs = lastDataRefreshAt ? Date.now() - lastDataRefreshAt.getTime() : null;
  const dataIsStale = loadError || (refreshAgeMs != null && refreshAgeMs > AUTO_REFRESH_MS * 3);
  const formatCurrency = (value) => new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value || 0);

  return (
    <div className="page dashboard-page">
      <section className="page-hero">
        <div>
          <div className="eyebrow"><span className="live-dot" /> Portfolio command center</div>
          <h1>Good decisions start with a clear view.</h1>
          <p>Track exposure, protect open trades, and keep your risk controls within reach.</p>
        </div>
        <div className="dashboard-actions">
          <button className="button button-primary refresh-button" onClick={load} disabled={loading || Boolean(bulkReset)}>
            <span className={loading ? 'refresh-icon spinning' : 'refresh-icon'}>&#8635;</span>
            {loading ? 'Refreshing' : 'Refresh data'}
          </button>
          <button className="button button-secondary" onClick={() => runBulkReset('live-reset')} disabled={Boolean(bulkReset)}>
            {bulkReset === 'live-reset' ? 'Resetting...' : 'Reset live execution all'}
          </button>
          <button className="button button-secondary" onClick={() => runBulkReset('notification-reset')} disabled={Boolean(bulkReset)}>
            {bulkReset === 'notification-reset' ? 'Resetting...' : 'Reset notifications all'}
          </button>
          <button className="button button-secondary" onClick={() => runBulkReset('live-enable')} disabled={Boolean(bulkReset)}>
            {bulkReset === 'live-enable' ? 'Enabling...' : 'Enable live execution all'}
          </button>
          <button className="button button-secondary" onClick={() => runBulkReset('live-disable')} disabled={Boolean(bulkReset)}>
            {bulkReset === 'live-disable' ? 'Disabling...' : 'Disable live execution all'}
          </button>
          <button className="button button-secondary" onClick={() => runBulkReset('notification-enable')} disabled={Boolean(bulkReset)}>
            {bulkReset === 'notification-enable' ? 'Enabling...' : 'Enable notifications all'}
          </button>
          <button className="button button-secondary" onClick={() => runBulkReset('notification-disable')} disabled={Boolean(bulkReset)}>
            {bulkReset === 'notification-disable' ? 'Disabling...' : 'Disable notifications all'}
          </button>
        </div>
      </section>

      {bulkResetMessage ? <div className="notice notice-success" role="status"><strong>{bulkResetMessage}</strong></div> : null}
      {bulkResetError ? <div className="notice notice-warning" role="alert"><strong>Bulk reset failed.</strong><span>{bulkResetError}</span></div> : null}

      <div className={`dashboard-sync-status ${dataIsStale ? 'is-stale' : ''}`} role="status" aria-live="polite">
        <span className="sync-dot" aria-hidden="true" />
        <span>{dataIsStale ? 'Live data may be stale' : 'Live data updating automatically'}</span>
        <small>
          {lastDataRefreshAt
            ? `Last dashboard sync: ${lastDataRefreshAt.toLocaleTimeString()}`
            : 'Waiting for first dashboard sync'}
          {' · '}Every {AUTO_REFRESH_MS / 1000}s
        </small>
      </div>

      {loadError ? (
        <div className="notice notice-warning" role="status">
          <strong>Live data is unavailable.</strong>
          <span>Check that the backend is running and your Kite session is active.</span>
        </div>
      ) : null}

      <RealSummaryCards
        {...summaryMetrics}
        formatCurrency={formatCurrency}
        plannedRiskBreakdown={summaryMetrics.plannedRiskBreakdown}
        worstCaseWalletBreakdown={summaryMetrics.worstCaseWalletBreakdown}
      />

      {activeTab === 'config' ? (
        <TradeConfigCard config={tradeConfig} onChanged={setTradeConfig} />
      ) : (
        <>
          <div className="cards column">
            <PositionsCard
              positions={unifiedLongPositions}
              onSaved={load}
              loading={loading}
              tradeConfig={tradeConfig}
              title="Long positions"
              kicker="Open CNC buys and Holdings combined by stock"
              emptyTitle="No long positions to show"
              emptyText="Long stock positions from Kite will appear here after your account syncs."
            />
          </div>
        </>
      )}
    </div>
  );
}
